import { db } from "./db";
import { summarize } from "./summarize";
import { sha256Blob, sha256Text, stamp, tz, uuid, kindOf, docName, extOf, normalizeFile } from "./util";
import { creatorStamp, stampPhoto, videoThumb } from "./watermark";

export const EMPTY_RECEIPT = { vendor: "", amount: "", invoice: "", date: "", carrier: "", tracking: "", status: "" };
export const STATUSES = ["Ordered", "Shipped", "In Transit", "Delivered", "Returned", "Disputed"];

export async function createNote(folderId, data = {}) {
  const now = new Date().toISOString();
  const note = {
    id: uuid(), folderId, title: "", body: "", ref: "", receipt: { ...EMPTY_RECEIPT },
    createdAt: now, updatedAt: now, versionCount: 0, lastHash: "", ...data,
  };
  note.summary = summarize(note.body, note.ref);
  await db.put("notes", note);
  return note;
}

export async function saveDraft(note) {
  const n = { ...note, summary: summarize(note.body, note.ref), updatedAt: new Date().toISOString() };
  await db.put("notes", n);
  return n;
}

export const listVersions = async (noteId) =>
  (await db.byIndex("versions", "noteId", noteId)).sort((a, b) => a.savedAt.localeCompare(b.savedAt));

const canonical = (v) =>
  JSON.stringify([v.noteId, v.n, v.title, v.body, v.ref, v.receipt, v.folder, v.savedAt, v.localTime, v.tz, v.creatorStamp, v.attachments, v.prevHash, ...(v.meta ? [v.meta] : [])]);
const META_KEYS = ["context", "apptAt", "location", "scheduledFor", "entryType", "deadline", "healthDate", "tags"];

export const versionPatch = (v) => ({
  title: v.title || "", body: v.body || "", ref: v.ref || "",
  receipt: { ...EMPTY_RECEIPT, ...v.receipt }, ...(v.meta || {}),
});
export const differsFrom = (note, v) => {
  const p = versionPatch(v);
  return Object.keys(p).some((k) => JSON.stringify(p[k]) !== JSON.stringify(k === "receipt" ? { ...EMPTY_RECEIPT, ...note.receipt } : (note[k] || "")));
};

export const verifyVersion = async (v) => (await sha256Text(canonical(v))) === v.hash;

export async function stampVersion(note, profile, folderName) {
  const existing = await listVersions(note.id);
  const last = existing[existing.length - 1];
  const n = existing.reduce((m, v) => Math.max(m, v.n), 0) + 1;
  const files = (await db.byIndex("files", "noteId", note.id)).filter((f) => f.role === "original");
  const d = new Date();
  const content = {
    noteId: note.id, n, title: note.title, body: note.body, ref: note.ref,
    receipt: { ...EMPTY_RECEIPT, ...note.receipt }, folder: folderName,
    savedAt: d.toISOString(), localTime: stamp(d), tz: tz(), creatorStamp: creatorStamp(profile),
    attachments: files.map((f) => ({ id: f.id, name: f.name, size: f.size, hash: f.hash })),
    prevHash: last?.hash || null,
    meta: Object.fromEntries(META_KEYS.map((k) => [k, note[k] || ""])),
  };
  const v = { id: uuid(), ...content, hash: await sha256Text(canonical(content)) };
  await db.addVersion(v);
  const saved = await saveDraft({ ...note, versionCount: n, lastHash: v.hash });
  return { version: v, note: saved };
}

export const listFiles = async (noteId) =>
  (await db.byIndex("files", "noteId", noteId)).sort((a, b) => a.createdAt.localeCompare(b.createdAt));

async function putDerived(parent, role, blob, extra = {}) {
  const rec = {
    id: uuid(), noteId: parent.noteId, parentId: parent.id, role, kind: "photo",
    name: `${role}_${parent.name}.jpg`, mime: "image/jpeg", size: blob.size,
    hash: await sha256Blob(blob), createdAt: new Date().toISOString(), blob, ...extra,
  };
  await db.put("files", rec);
  return rec;
}

export async function addAttachment(note, raw, ctx) {
  const file = await normalizeFile(raw);
  const date = new Date().toISOString();
  const orig = {
    id: uuid(), noteId: note.id, role: "original", kind: kindOf(file.type, file.name),
    name: file.name, mime: file.type || "application/octet-stream", size: file.size,
    hash: await sha256Blob(file), createdAt: date, blob: file,
  };
  await db.put("files", orig);
  const sctx = { ...ctx, date, title: note.title, ref: note.ref };
  if (orig.kind === "photo") {
    const s = await stampPhoto(file, sctx);
    if (s) await putDerived(orig, "stamped", s);
  } else if (orig.kind === "video") {
    const t = await videoThumb(file, sctx);
    if (t) await putDerived(orig, "thumb", t);
  }
  return orig;
}

export const saveFrame = (parent, blob, tc) => putDerived(parent, "frame", blob, { timecode: tc });

export async function deleteNote(id) {
  for (const f of await db.byIndex("files", "noteId", id)) await db.del("files", f.id);
  for (const v of await db.byIndex("versions", "noteId", id)) await db.del("versions", v.id);
  await db.del("notes", id);
}

export function fileDocName(file, note, folderName, profile) {
  const ext = file.role === "original" ? extOf(file.name, file.mime) : "jpg";
  const suffix = { stamped: "STAMPED", thumb: "THUMB", frame: `FRAME-${(file.timecode || "").replace(/[:.]/g, "")}` }[file.role];
  return docName({ profile, folderName, title: note.title, ref: note.ref || note.summary?.ref, date: file.createdAt, ext, suffix });
}

export function buildIndex(folder, notes) {
  const sorted = [...notes].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const lines = [
    `Folder Summary — ${folder.name}`,
    `Generated ${stamp()} • ${sorted.length} entr${sorted.length === 1 ? "y" : "ies"}`,
    ``,
  ];
  sorted.forEach((n, i) => {
    const s = n.summary || {};
    const ref = n.ref || s.ref;
    const extra = [n.context, n.apptAt && `when: ${n.apptAt.replace("T", " ")}`, n.location && `at: ${n.location}`, n.receipt?.vendor && `vendor: ${n.receipt.vendor}`, n.receipt?.amount && `$${n.receipt.amount}`, s.date && `date mentioned: ${s.date}`, n.receipt?.status && `status: ${n.receipt.status}`, n.receipt?.tracking && `tracking: ${n.receipt.tracking}`].filter(Boolean).join(" · ");
    const title = n.title || `${folder.name} - ${stamp(new Date(n.createdAt)).slice(0, 10)}`;
    lines.push(`${i + 1}. ${stamp(new Date(n.createdAt))}${ref ? ` · Ref: ${ref}` : ""} · v${n.versionCount || 0} — ${title}`);
    const line2 = [s.sentence, extra && `(${extra})`].filter(Boolean).join("  ");
    if (line2) lines.push(`   ${line2}`);
  });
  if (!sorted.length) lines.push("No entries yet.");
  return lines.join("\n");
}
